import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { signIn, signUp, resetPassword } from '../utils/auth';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiAlertCircle, FiCheckCircle } = FiIcons;

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isReset, setIsReset] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isReset) {
        const { error: resetError } = await resetPassword(email);
        if (resetError) throw resetError;
        setSuccess('Reset-Link wurde versendet. Bitte prüfen Sie Ihr Postfach.');
        setEmail('');
      } else if (isLogin) {
        const { error: signInError } = await signIn(email, password);
        if (signInError) throw signInError;
        navigate('/portal');
      } else {
        if (password !== confirmPassword) {
          throw new Error('Passwörter stimmen nicht überein');
        }
        if (password.length < 6) {
          throw new Error('Passwort muss mindestens 6 Zeichen lang sein');
        }
        const { error: signUpError } = await signUp(email, password);
        if (signUpError) throw signUpError;
        setSuccess('Registrierung erfolgreich! Bitte prüfen Sie Ihre E-Mail zur Verifizierung.');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setError(err.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-light)] px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="neon-border-container bg-white p-8 rounded-xl shadow-lg">
          <div className="text-center mb-8">
            <img 
              src="https://meinerfahrungsbericht.de/logo-me/me-logo.png" 
              alt="meLogin Logo" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-[var(--color-text-dark)] mb-2">
              {isReset ? 'Passwort zurücksetzen' : isLogin ? 'Willkommen zurück' : 'Jetzt registrieren'}
            </h1>
            <p className="text-[var(--color-text-light)]">
              {isReset 
                ? 'Geben Sie Ihre E-Mail-Adresse ein' 
                : isLogin 
                ? 'Melden Sie sich bei Ihrem Konto an' 
                : 'Erstellen Sie Ihr kostenloses Konto'}
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
            >
              <SafeIcon icon={FiAlertCircle} className="text-red-500 text-xl flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3"
            >
              <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{success}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[var(--color-text-dark)] mb-2">
                E-Mail-Adresse
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--color-text-light)]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-12"
                  placeholder="ihre@email.de"
                  required
                />
              </div>
            </div>

            {!isReset && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-[var(--color-text-dark)] mb-2">
                  Passwort
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--color-text-light)]" />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-12"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
            )}

            {!isLogin && !isReset && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-[var(--color-text-dark)] mb-2">
                  Passwort bestätigen
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--color-text-light)]" />
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field pl-12"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
            )}

            {isLogin && !isReset && (
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setIsReset(true)}
                  className="text-[var(--color-primary)] hover:underline"
                >
                  Passwort vergessen?
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Bitte warten...' : isReset ? 'Reset-Link senden' : isLogin ? 'Anmelden' : 'Jetzt registrieren'}
            </button>
          </form>

          <div className="mt-6 text-center">
            {isReset ? (
              <button
                onClick={() => {
                  setIsReset(false);
                  setError('');
                  setSuccess('');
                }}
                className="text-sm text-[var(--color-secondary)] hover:underline"
              >
                Zurück zur Anmeldung
              </button>
            ) : (
              <>
                <p className="text-sm text-[var(--color-text-light)]">
                  {isLogin ? 'Noch kein Konto?' : 'Bereits registriert?'}
                </p>
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setSuccess('');
                  }}
                  className="text-sm text-[var(--color-primary)] hover:underline font-medium mt-1"
                >
                  {isLogin ? 'Jetzt registrieren' : 'Zur Anmeldung'}
                </button>
              </>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-light)] text-center">
              Mit der Anmeldung akzeptieren Sie unsere{' '}
              <Link to="/datenschutz" className="text-[var(--color-primary)] hover:underline">
                Datenschutzerklärung
              </Link>
              {' '}und{' '}
              <Link to="/agb" className="text-[var(--color-primary)] hover:underline">
                AGB
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;